<template>
  <ShareActionSheet v-model:show="show" @close="closeShare" @share-whatsapp="onShareChannel('whatsapp')"
    @share-telegram="onShareChannel('telegram')" @copy-link="onCopyLink" @save-image="onSaveImage">
    <template #preview>
      <TeamSharePoster v-if="isTeamScene" :export-mode="false" :players="stateData.players"
        :match-info="stateData.matchInfo" :team-meta="stateData.teamMeta" :user-info="stateData.userInfo"
        :invite-info="stateData.inviteInfo" :qr-data-url="stateData.qrDataUrl" />
      <MatchSharePoster v-else :data="stateData" />
    </template>
  </ShareActionSheet>

  <div class="share-offscreen">
    <TeamSharePoster v-if="isTeamScene" ref="teamPosterRef" :export-mode="true" :players="stateData.players"
      :match-info="stateData.matchInfo" :team-meta="stateData.teamMeta" :user-info="stateData.userInfo"
      :invite-info="stateData.inviteInfo" :qr-data-url="stateData.qrDataUrl" />
    <MatchSharePoster v-else ref="matchPosterRef" :data="stateData" />
  </div>
</template>

<script setup>
import { computed, reactive, ref, watch } from 'vue'
import ShareActionSheet from './ShareActionSheet.vue'
import MatchSharePoster from './posters/MatchSharePoster.vue'
import TeamSharePoster from './posters/TeamSharePoster.vue'
import { closeShare, useShareState } from '@/composables/share/useShareState'
import { buildQrCode, capturePoster, dataUrlToBase64, downloadImageByPlatform } from '@/composables/share/useShareCapture'
import { handleCopyLink, shareByChannel } from '@/composables/share/useShareActions'
import { buildSharePayload, ensureTeamPlayers, fetchInviteInfo, getShareLink } from '@/composables/share/useShareData'
import { useStore } from '@/stores'
import { hideLoading, showLoading, showToast } from '@/utils/message'

const store = useStore()
const shareState = useShareState()

const show = computed({
  get: () => shareState.show,
  set: (val) => {
    if (!val) closeShare()
  },
})

const isTeamScene = computed(() => shareState.context.scene === 'team')

const teamPosterRef = ref(null)
const matchPosterRef = ref(null)

const stateData = reactive({
  scene: 'team',
  title: '',
  desc: '',
  matchInfo: {},
  roomInfo: {},
  teamMeta: {},
  userInfo: {},
  players: [],
  inviteInfo: {
    invCode: '',
    il: '',
  },
  qrDataUrl: '',
  finalShareLink: '',
})
const hydrateShareData = async () => {
  const context = shareState.context || {}
  const payload = buildSharePayload(context.scene, context.payload)
  const inviteInfo = await fetchInviteInfo()
  const players = context.scene === 'team' ? await ensureTeamPlayers({ ...payload.teamMeta, players: payload.players }) : []

  const finalShareLink = getShareLink({
    apiLink: inviteInfo.il || payload.shareLink,
    scene: payload.scene,
    matchId: payload.matchId,
    teamId: payload.teamId,
  })
  const qrDataUrl = await buildQrCode(finalShareLink)

  stateData.scene = payload.scene
  stateData.title = payload.title
  stateData.desc = payload.desc
  stateData.matchInfo = payload.matchInfo
  stateData.roomInfo = payload.roomInfo
  stateData.teamMeta = payload.teamMeta
  stateData.userInfo = payload.userInfo
  stateData.players = players
  stateData.inviteInfo = inviteInfo
  stateData.qrDataUrl = qrDataUrl
  stateData.finalShareLink = finalShareLink
}

watch(
  () => shareState.show,
  (visible) => {
    if (visible) {
      hydrateShareData()
    }
  },
)

watch(
  () => shareState.context,
  () => {
    if (shareState.show) {
      hydrateShareData()
    }
  },
  { deep: true },
)

const onShareChannel = async (channel) => {
  await shareByChannel(channel, {
    title: stateData.title,
    desc: stateData.desc,
    link: stateData.finalShareLink,
    scene: stateData.scene,
  }, {
    deviceType: store.P_PARAMS?.deviceType,
  })
}

const onCopyLink = async () => {
  await handleCopyLink(stateData.finalShareLink)
}

const getPosterElement = () => {
  if (isTeamScene.value) {
    return teamPosterRef.value?.getPosterElement?.()
  }
  return matchPosterRef.value?.getPosterElement?.()
}

const onSaveImage = async () => {
  showLoading()
  try {
    const el = getPosterElement()
    const dataUrl = await capturePoster(el, { backgroundColor: '#ffffff' })
    const base64 = dataUrlToBase64(dataUrl)
    await downloadImageByPlatform({
      base64,
      fileName: `${stateData.scene}-share-${Date.now()}.png`,
      deviceType: store.P_PARAMS?.deviceType || '',
      version: store.P_PARAMS?.version || 0,
      shareLink: stateData.finalShareLink,
    })
    showToast.success('Image saved')
  }
  catch (error) {
    console.error('[share] save image failed:', error)
    showToast.warning('Save image failed in current environment')
  }
  finally {
    hideLoading()
  }
}
</script>

<style scoped lang="less">
.share-offscreen {
  position: fixed;
  left: -99999px;
  top: -99999px;
  z-index: -1;
  pointer-events: none;
}
</style>
